/**
 * CX Card / Product Slider: arrows, drag scroll, auto-scroll, mobile grid toggle. Self-contained.
 */
(function () {
  function queryAll(root, selector) {
    if (!root || typeof root.querySelectorAll !== 'function') return [];
    return Array.from(root.querySelectorAll(selector));
  }
  function querySelfAndAll(root, selector) {
    const out = [];
    if (root instanceof Element && root.matches(selector)) out.push(root);
    out.push(...queryAll(root, selector));
    return out;
  }
  let _reducedMotion = false;
  let _reducedMotionBound = false;
  function ensureReducedMotionCache() {
    if (_reducedMotionBound) return;
    _reducedMotionBound = true;
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    _reducedMotion = !!mql.matches;
    const onChange = (e) => { _reducedMotion = !!e.matches; };
    if (typeof mql.addEventListener === 'function') mql.addEventListener('change', onChange);
    else if (typeof mql.addListener === 'function') mql.addListener(onChange);
  }
  function prefersReducedMotion() { return _reducedMotion; }

  const CX_CARD_SLIDER_SECTION_SELECTOR = '[data-cx-card-slider="true"]';
  const CX_CARD_SLIDER_TRACK_SELECTOR = '.custom-container__cards';
  const CX_CARD_SLIDER_ARROW_SELECTOR = '.custom-container__arrow';
  const CX_CARD_SLIDER_CARD_SELECTOR = '.custom-container__card';
  const CX_CARD_SLIDER_ARROW_BOUND_FLAG = 'cxCardSliderBound';
  const CX_CARD_SLIDER_TRACK_BOUND_FLAG = 'cxCardSliderTrackBound';
  const CX_CARD_SLIDER_CLICK_BLOCK_FLAG = 'cxCardSliderBlockClick';
  const CX_CARD_SLIDER_LEAD_OVERLAY_ACTIVE_CLASS = 'cx-product-slider--lead-overlay-active';
  const CX_CARD_SLIDER_MOBILE_GRID_TOGGLE_SELECTOR = '[data-cx-mobile-grid-toggle="true"]';
  const CX_CARD_SLIDER_MOBILE_GRID_TOGGLE_BOUND_FLAG = 'cxMobileGridToggleBound';
  const CX_CARD_SLIDER_MOBILE_GRID_EXPANDED_CLASS = 'cx-mobile-grid--expanded';

  const CX_CARD_SLIDER_AUTO_SCROLL_TIMER = new WeakMap();
  const CX_CARD_SLIDER_TRACK_TILE_DELTA_CACHE = new WeakMap();
  const CX_CARD_SLIDER_TRACK_RESIZE_OBSERVER = new WeakMap();
  const CX_CARD_SLIDER_INFINITE_SEGMENT_WIDTH = new WeakMap();
  const CX_CARD_SLIDER_BOUND_TRACKS = new Set();
  let CX_CARD_SLIDER_WINDOW_RESIZE_BOUND = false;
  const CX_CARD_SLIDER_INFINITE_READY = 'data-cx-infinite-ready';
  const CX_CARD_SLIDER_CLONE_MARKER = 'data-cx-infinite-clone';
  const CX_CARD_SLIDER_VIEWPORT_MARGIN = '200px 0px';
  const CX_CARD_SLIDER_PENDING_SECTIONS = new WeakSet();
  let CX_CARD_SLIDER_INTERSECTION_OBSERVER = null;
  let CX_CARD_SLIDER_INTERACTION_BOUND = false;

  /** @param {HTMLElement} el */
  function cxCardSliderGetGapPx(el) {
    const styles = window.getComputedStyle(el);
    const raw = styles.columnGap || styles.gap || '0';
    const gap = Number.parseFloat(raw);
    return Number.isFinite(gap) ? gap : 0;
  }

  /** @param {HTMLElement} track */
  function cxCardSliderGetScrollAmountPx(track) {
    const cached = CX_CARD_SLIDER_TRACK_TILE_DELTA_CACHE.get(track);
    if (typeof cached === 'number' && cached > 0) return cached;

    /** @type {HTMLElement | null} */
    const firstCard = /** @type {any} */ (track.querySelector(CX_CARD_SLIDER_CARD_SELECTOR) || track.firstElementChild);
    if (!firstCard) return 0;
    const cardWidth = firstCard.getBoundingClientRect().width || firstCard.offsetWidth || 0;
    const gap = cxCardSliderGetGapPx(track);
    const delta = cardWidth + gap;
    if (delta > 0) CX_CARD_SLIDER_TRACK_TILE_DELTA_CACHE.set(track, delta);
    return delta;
  }

  /** @param {HTMLElement} track */
  function cxCardSliderGetScrollDeltaPx(track) {
    const mode = (track.dataset.scrollMode || 'tile').toLowerCase();
    if (mode === 'viewport') return track.clientWidth || track.getBoundingClientRect().width || 0;
    return cxCardSliderGetScrollAmountPx(track);
  }

  /**
   * @param {HTMLElement} track
   * @returns {number | null} segment width for infinite scroll, or null
   */
  function cxCardSliderGetInfiniteSegmentWidth(track) {
    if (!track.hasAttribute(CX_CARD_SLIDER_INFINITE_READY)) return null;
    const cached = CX_CARD_SLIDER_INFINITE_SEGMENT_WIDTH.get(track);
    if (typeof cached === 'number' && cached > 0) return cached;
    const seg = Number.parseFloat(track.dataset.cxInfiniteSegmentWidth || '0');
    if (Number.isFinite(seg) && seg > 0) return seg;
    return null;
  }

  /**
   * @param {HTMLElement} track
   * @param {'left' | 'right'} [direction]
   */
  function cxCardSliderScrollNext(track, direction = 'right') {
    const delta = cxCardSliderGetScrollDeltaPx(track);
    if (!delta) return;

    const behavior = prefersReducedMotion() ? 'auto' : 'smooth';
    const sign = direction === 'left' ? -1 : 1;
    const segmentWidth = cxCardSliderGetInfiniteSegmentWidth(track);
    const maxScrollLeft = track.scrollWidth - track.clientWidth;
    const atEnd = track.scrollLeft >= maxScrollLeft - 1;
    const atStart = track.scrollLeft <= 1;

    if (segmentWidth != null) {
      if (direction === 'left' && atStart) {
        track.scrollTo({ left: segmentWidth - delta, behavior: 'auto' });
        requestAnimationFrame(() => {
          track.scrollBy({ left: -delta, behavior });
        });
        return;
      }
    } else {
      if (direction !== 'left' && atEnd) return;
      if (direction === 'left' && atStart) return;
    }

    track.scrollBy({ left: sign * delta, behavior });
  }

  /** @param {HTMLElement} track */
  function cxCardSliderComputeEdgeState(track) {
    const segmentWidth = cxCardSliderGetInfiniteSegmentWidth(track);
    const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
    let atStart = track.scrollLeft <= 1;
    let atEnd = track.scrollLeft >= maxScrollLeft - 1;
    if (segmentWidth != null) {
      atEnd = false;
      atStart = false;
    }
    return {
      maxScrollLeft,
      atStart,
      atEnd
    };
  }

  /**
   * When auto-scroll is enabled, duplicate track content so we can loop seamlessly.
   * @param {HTMLElement} track
   */
  function cxCardSliderSetupInfiniteScroll(track) {
    if (track.hasAttribute(CX_CARD_SLIDER_INFINITE_READY)) return;
    const enabled = track.dataset.autoScrollEnabled === 'true';
    if (!enabled) return;

    const cards = queryAll(track, CX_CARD_SLIDER_CARD_SELECTOR);
    if (!cards.length) return;

    const fragment = document.createDocumentFragment();
    for (const card of cards) {
      const clone = card.cloneNode(true);
      if (clone instanceof HTMLElement) clone.setAttribute(CX_CARD_SLIDER_CLONE_MARKER, 'true');
      fragment.appendChild(clone);
    }
    track.appendChild(fragment);

    track.setAttribute(CX_CARD_SLIDER_INFINITE_READY, 'true');

    function storeSegmentWidth() {
      const firstClone = track.querySelector(`[${CX_CARD_SLIDER_CLONE_MARKER}="true"]`);
      if (firstClone instanceof HTMLElement) {
        const w = firstClone.offsetLeft;
        if (Number.isFinite(w) && w > 0) {
          CX_CARD_SLIDER_INFINITE_SEGMENT_WIDTH.set(track, w);
          track.dataset.cxInfiniteSegmentWidth = String(w);
        }
      }
      if (!CX_CARD_SLIDER_INFINITE_SEGMENT_WIDTH.get(track)) {
        const half = track.scrollWidth / 2;
        if (Number.isFinite(half) && half > 0) {
          CX_CARD_SLIDER_INFINITE_SEGMENT_WIDTH.set(track, half);
          track.dataset.cxInfiniteSegmentWidth = String(Math.round(half));
        }
      }
    }

    requestAnimationFrame(storeSegmentWidth);
    if (typeof ResizeObserver === 'function') {
      const ro = new ResizeObserver(storeSegmentWidth);
      ro.observe(track);
    }
  }

  const CX_CARD_SLIDER_ARROWS_WRAPPER_HIDDEN_CLASS = 'custom-container__arrows--hidden';

  /** @param {HTMLElement} track */
  function cxCardSliderUpdateArrowsForTrack(track) {
    const section = track.closest(CX_CARD_SLIDER_SECTION_SELECTOR);
    if (!(section instanceof Element)) return;

    const trackId = track.id;
    if (!trackId) return;

    const { maxScrollLeft, atStart, atEnd } = cxCardSliderComputeEdgeState(track);
    const noScroll = maxScrollLeft <= 1;

    const arrowsWrapper = section.querySelector('.custom-container__arrows');
    if (arrowsWrapper instanceof HTMLElement) {
      arrowsWrapper.classList.toggle(CX_CARD_SLIDER_ARROWS_WRAPPER_HIDDEN_CLASS, noScroll);
    }

    section.querySelectorAll(CX_CARD_SLIDER_ARROW_SELECTOR).forEach((arrow) => {
      if (!(arrow instanceof HTMLButtonElement)) return;
      if (arrow.dataset.target !== trackId) return;

      const dir = arrow.dataset.direction === 'left' ? 'left' : 'right';
      const shouldDisable = noScroll || (dir === 'left' ? atStart : atEnd);
      arrow.disabled = shouldDisable;
    });
  }

  /** @param {Element} arrow */
  function cxCardSliderBindArrow(arrow) {
    if (!(arrow instanceof HTMLElement)) return;
    if (arrow.dataset[CX_CARD_SLIDER_ARROW_BOUND_FLAG] === 'true') return;
    arrow.dataset[CX_CARD_SLIDER_ARROW_BOUND_FLAG] = 'true';

    arrow.addEventListener('click', () => {
      if (arrow instanceof HTMLButtonElement && arrow.disabled) return;
      /** @type {'left' | 'right'} */
      const direction = arrow.dataset.direction === 'left' ? 'left' : 'right';
      const targetId = arrow.dataset.target;

      const trackEl =
        (targetId && document.getElementById(targetId)) ||
        arrow.closest(CX_CARD_SLIDER_SECTION_SELECTOR)?.querySelector(CX_CARD_SLIDER_TRACK_SELECTOR) ||
        arrow.closest('.custom-container')?.querySelector(CX_CARD_SLIDER_TRACK_SELECTOR);

      if (!(trackEl instanceof HTMLElement)) return;
      cxCardSliderScrollNext(trackEl, direction);
      cxCardSliderUpdateArrowsForTrack(trackEl);
    });
  }

  /** @param {HTMLElement} track */
  function cxCardSliderBindAutoScroll(track) {
    if (!track) return;

    function clear() {
      const existing = CX_CARD_SLIDER_AUTO_SCROLL_TIMER.get(track);
      if (existing) window.clearInterval(existing);
      CX_CARD_SLIDER_AUTO_SCROLL_TIMER.delete(track);
    }

    function start() {
      clear();
      const enabled = track.dataset.autoScrollEnabled === 'true';
      if (!enabled) return;

      const interval = Number.parseInt(track.dataset.autoScrollInterval || '4000', 10);
      const ms = Number.isFinite(interval) && interval >= 500 ? interval : 4000;

      const id = window.setInterval(() => {
        const paused = track.dataset.cxAutoScrollPaused === 'true';
        const interacting = track.classList.contains('is-pointer-down') || track.classList.contains('is-dragging');
        if (paused || interacting) return;
        cxCardSliderScrollNext(track, 'right');
        cxCardSliderUpdateArrowsForTrack(track);
      }, ms);

      CX_CARD_SLIDER_AUTO_SCROLL_TIMER.set(track, id);
    }

    track.addEventListener('mouseenter', () => {
      track.dataset.cxAutoScrollPaused = 'true';
    });
    track.addEventListener('mouseleave', () => {
      track.dataset.cxAutoScrollPaused = 'false';
    });

    start();
  }

  /** @param {HTMLElement} track */
  function cxCardSliderBindDrag(track) {
    if (!track) return;

    let isPointerDown = false;
    let isDragging = false;
    let startX = 0;
    let startScrollLeft = 0;
    let lastX = 0;
    let lastT = 0;
    let velocity = 0;
    let inertiaRaf = 0;
    /** @type {string} */
    let activePointerType = 'mouse';
    /** @type {number | null} */
    let activePointerId = null;

    function cancelInertia() {
      if (inertiaRaf) cancelAnimationFrame(inertiaRaf);
      inertiaRaf = 0;
    }

    const INTERACTIVE_SELECTOR = [
      'button', 'input', 'textarea', 'select', 'option', 'label', 'summary', 'details',
      '[role="button"]', '[role="link"]',
      'swatches-variant-picker-component', 'overflow-list', 'card-gallery', 'slideshow-component', 'slideshow-controls', 'slideshow-arrows',
      '[data-variant-id]', '[data-option-media-id]', '[data-cx-no-drag]', '[on\\:click]'
    ].join(',');

    /** @param {PointerEvent} e */
    function isInteractivePointerStart(e) {
      const path = typeof e.composedPath === 'function' ? e.composedPath() : [];
      for (const node of path) {
        if (!(node instanceof Element)) continue;
        if (node.matches(INTERACTIVE_SELECTOR)) return true;
      }

      const target = e.target;
      if (!(target instanceof Element)) return false;
      return !!target.closest(INTERACTIVE_SELECTOR);
    }

    function setClickBlockOnce() {
      track.dataset[CX_CARD_SLIDER_CLICK_BLOCK_FLAG] = 'true';
      window.setTimeout(() => {
        track.dataset[CX_CARD_SLIDER_CLICK_BLOCK_FLAG] = 'false';
      }, 0);
    }

    /** @param {MouseEvent} e */
    function onClickCapture(e) {
      if (track.dataset[CX_CARD_SLIDER_CLICK_BLOCK_FLAG] !== 'true') return;
      if (!(e.target instanceof Element)) return;

      if (e.target.closest(INTERACTIVE_SELECTOR)) return;

      const link = e.target.closest('a[href]');
      if (!link) return;

      e.preventDefault();
      e.stopPropagation();
    }

    track.addEventListener('click', onClickCapture, true);

    track.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      if (isInteractivePointerStart(e)) return;

      activePointerType = e.pointerType || 'mouse';
      activePointerId = e.pointerId;

      if (activePointerType === 'touch') return;

      cancelInertia();

      isPointerDown = true;
      isDragging = false;
      startX = e.clientX;
      startScrollLeft = track.scrollLeft;
      lastX = e.clientX;
      lastT = performance.now();
      velocity = 0;

      track.classList.add('is-pointer-down');
    });

    track.addEventListener('pointermove', (e) => {
      if (!isPointerDown) return;

      const dx = e.clientX - startX;
      if (!isDragging && Math.abs(dx) > 6) {
        isDragging = true;
        track.classList.add('is-dragging');
        if (activePointerId != null) {
          track.setPointerCapture?.(activePointerId);
        }
      }

      if (!isDragging) return;

      const now = performance.now();
      const dt = now - lastT || 16;
      const moveX = e.clientX - lastX;
      velocity = moveX / dt;
      lastX = e.clientX;
      lastT = now;

      track.scrollLeft = startScrollLeft - dx;
      setClickBlockOnce();
    });

    /** @param {PointerEvent} e */
    function endPointer(e) {
      if (!isPointerDown) return;
      isPointerDown = false;
      track.classList.remove('is-pointer-down');
      if (activePointerId != null) {
        track.releasePointerCapture?.(activePointerId);
      }
      activePointerId = null;

      if (!isDragging) {
        track.classList.remove('is-dragging');
        return;
      }

      track.classList.remove('is-dragging');

      if (prefersReducedMotion()) return;

      let v = -velocity;
      if (!Number.isFinite(v) || Math.abs(v) < 0.05) return;

      const friction = 0.92;
      let last = performance.now();

      const step = () => {
        const now = performance.now();
        const dt = now - last;
        last = now;

        track.scrollLeft += v * dt;
        v *= friction;

        if (Math.abs(v) < 0.02) {
          inertiaRaf = 0;
          return;
        }
        inertiaRaf = requestAnimationFrame(step);
      };

      inertiaRaf = requestAnimationFrame(step);
    }

    track.addEventListener('pointerup', endPointer);
    track.addEventListener('pointercancel', endPointer);
    track.addEventListener('pointerleave', (e) => {
      if (isPointerDown && e.pointerType === 'mouse') endPointer(e);
    });
  }

  /** @param {HTMLElement} track */
  function cxCardSliderBindTrack(track) {
    if (!track || track.dataset[CX_CARD_SLIDER_TRACK_BOUND_FLAG] === 'true') return;
    track.dataset[CX_CARD_SLIDER_TRACK_BOUND_FLAG] = 'true';

    /** @type {HTMLElement | null} */
    const section = /** @type {any} */ (track.closest(CX_CARD_SLIDER_SECTION_SELECTOR));
    const leadOverlayEnabled = section?.dataset?.cxLeadOverlay === 'true';

    function updateLeadOverlayState() {
      if (!leadOverlayEnabled) return;
      if (!(section instanceof HTMLElement)) return;
      section.classList.toggle(CX_CARD_SLIDER_LEAD_OVERLAY_ACTIVE_CLASS, track.scrollLeft > 1);
    }

    let updateRaf = 0;
    function scheduleTrackUpdate() {
      if (updateRaf) return;
      updateRaf = requestAnimationFrame(() => {
        updateRaf = 0;
        cxCardSliderUpdateArrowsForTrack(track);
        updateLeadOverlayState();
      });
    }

    CX_CARD_SLIDER_BOUND_TRACKS.add(track);
    if (typeof ResizeObserver === 'function') {
      let initialResizeDelivered = false;
      const ro = new ResizeObserver(() => {
        CX_CARD_SLIDER_TRACK_TILE_DELTA_CACHE.delete(track);
        // observe() immediately reports the size already handled by the initial
        // scheduled update. Only subsequent size changes need another pass.
        if (!initialResizeDelivered) {
          initialResizeDelivered = true;
          return;
        }
        scheduleTrackUpdate();
      });
      ro.observe(track);
      CX_CARD_SLIDER_TRACK_RESIZE_OBSERVER.set(track, ro);
    } else if (!CX_CARD_SLIDER_WINDOW_RESIZE_BOUND) {
      CX_CARD_SLIDER_WINDOW_RESIZE_BOUND = true;
      window.addEventListener(
        'resize',
        () => {
          CX_CARD_SLIDER_BOUND_TRACKS.forEach((t) => CX_CARD_SLIDER_TRACK_TILE_DELTA_CACHE.delete(t));
        },
        { passive: true }
      );
    }

    cxCardSliderSetupInfiniteScroll(track);
    cxCardSliderBindDrag(track);
    cxCardSliderBindAutoScroll(track);

    scheduleTrackUpdate();
    if (!document.readyState || document.readyState !== 'complete') {
      window.addEventListener('load', scheduleTrackUpdate, { once: true });
    }

    let infiniteJumpInProgress = false;
    track.addEventListener(
      'scroll',
      () => {
        const segmentWidth = cxCardSliderGetInfiniteSegmentWidth(track);
        if (segmentWidth != null && !infiniteJumpInProgress && track.scrollLeft >= segmentWidth - 1) {
          infiniteJumpInProgress = true;
          track.scrollLeft = track.scrollLeft - segmentWidth;
          infiniteJumpInProgress = false;
        }
        scheduleTrackUpdate();
      },
      { passive: true }
    );
  }

  /** @param {Element} btn */
  function cxCardSliderBindMobileGridToggle(btn) {
    if (!(btn instanceof HTMLButtonElement)) return;
    if (btn.dataset[CX_CARD_SLIDER_MOBILE_GRID_TOGGLE_BOUND_FLAG] === 'true') return;
    btn.dataset[CX_CARD_SLIDER_MOBILE_GRID_TOGGLE_BOUND_FLAG] = 'true';

    btn.disabled = false;
    btn.removeAttribute('aria-disabled');

    btn.addEventListener('click', () => {
      const section = btn.closest(CX_CARD_SLIDER_SECTION_SELECTOR);
      if (!(section instanceof HTMLElement)) return;
      const isExpanded = section.classList.contains(CX_CARD_SLIDER_MOBILE_GRID_EXPANDED_CLASS);

      const showMoreText = btn.dataset.showMoreText || 'Show more';
      const showLessText = btn.dataset.showLessText || 'Show less';
      const track = section.querySelector(CX_CARD_SLIDER_TRACK_SELECTOR);
      const behavior = prefersReducedMotion() ? 'auto' : 'smooth';

      if (!isExpanded) {
        section.classList.add(CX_CARD_SLIDER_MOBILE_GRID_EXPANDED_CLASS);
        btn.textContent = showLessText;
        btn.setAttribute('aria-expanded', 'true');
        return;
      }

      section.classList.remove(CX_CARD_SLIDER_MOBILE_GRID_EXPANDED_CLASS);
      btn.textContent = showMoreText;
      btn.setAttribute('aria-expanded', 'false');

      if (track instanceof HTMLElement) track.scrollTo?.({ left: 0, behavior });

      if (track instanceof HTMLElement) {
        track.scrollIntoView?.({ block: 'start', behavior });
      } else {
        section.scrollIntoView?.({ block: 'start', behavior });
      }
    });
  }

  /** @param {Document | Element} root */
  function initCxCardSlider(root) {
    // Theme Editor performance: skip slider init (drag, arrows, auto-scroll) in design mode
    if (window.Shopify?.designMode) return;
    ensureReducedMotionCache();

    const sections = querySelfAndAll(root, CX_CARD_SLIDER_SECTION_SELECTOR);
    if (!sections.length) return;

    for (const section of sections) {
      section.querySelectorAll(CX_CARD_SLIDER_ARROW_SELECTOR).forEach(cxCardSliderBindArrow);
      section.querySelectorAll(CX_CARD_SLIDER_MOBILE_GRID_TOGGLE_SELECTOR).forEach(cxCardSliderBindMobileGridToggle);
      /** @type {HTMLElement | null} */
      const track = /** @type {any} */ (section.querySelector(CX_CARD_SLIDER_TRACK_SELECTOR));
      if (track) cxCardSliderBindTrack(track);
    }
  }

  /** @param {Element} section */
  function cxCardSliderSectionIsInitialized(section) {
    const track = section.querySelector(CX_CARD_SLIDER_TRACK_SELECTOR);
    return track instanceof HTMLElement && track.dataset[CX_CARD_SLIDER_TRACK_BOUND_FLAG] === 'true';
  }

  /** @param {Element} section */
  function cxCardSliderInitializeSection(section) {
    CX_CARD_SLIDER_INTERSECTION_OBSERVER?.unobserve?.(section);
    CX_CARD_SLIDER_PENDING_SECTIONS.delete(section);
    initCxCardSlider(section);
  }

  function cxCardSliderGetIntersectionObserver() {
    if (typeof IntersectionObserver !== 'function') return null;
    if (CX_CARD_SLIDER_INTERSECTION_OBSERVER) return CX_CARD_SLIDER_INTERSECTION_OBSERVER;

    CX_CARD_SLIDER_INTERSECTION_OBSERVER = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          if (!(entry.target instanceof Element)) continue;
          cxCardSliderInitializeSection(entry.target);
        }
      },
      { rootMargin: CX_CARD_SLIDER_VIEWPORT_MARGIN, threshold: 0.01 }
    );

    return CX_CARD_SLIDER_INTERSECTION_OBSERVER;
  }

  function cxCardSliderBindInteractionFallback() {
    if (CX_CARD_SLIDER_INTERACTION_BOUND) return;
    CX_CARD_SLIDER_INTERACTION_BOUND = true;

    const initializeFromEvent = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const section = target?.closest?.(CX_CARD_SLIDER_SECTION_SELECTOR);
      if (!(section instanceof Element)) return;
      if (!CX_CARD_SLIDER_PENDING_SECTIONS.has(section)) return;
      cxCardSliderInitializeSection(section);
    };

    document.addEventListener('pointerdown', initializeFromEvent, { capture: true, passive: true });
    document.addEventListener('focusin', initializeFromEvent, { capture: true });
  }

  /** @param {Document | Element} root */
  function scheduleCxCardSlider(root) {
    // Preserve the existing Theme Editor behavior from the current implementation.
    if (window.Shopify?.designMode) {
      initCxCardSlider(root);
      return;
    }

    ensureReducedMotionCache();
    cxCardSliderBindInteractionFallback();

    const sections = querySelfAndAll(root, CX_CARD_SLIDER_SECTION_SELECTOR);
    if (!sections.length) return;

    const observer = cxCardSliderGetIntersectionObserver();
    for (const section of sections) {
      if (!(section instanceof Element)) continue;

      // Re-run the idempotent binder for already-active sections so dynamic
      // arrows/toggles injected by Rebuy or section rendering are retained.
      if (cxCardSliderSectionIsInitialized(section)) {
        initCxCardSlider(section);
        continue;
      }

      if (!observer) {
        initCxCardSlider(section);
        continue;
      }

      if (CX_CARD_SLIDER_PENDING_SECTIONS.has(section)) continue;
      CX_CARD_SLIDER_PENDING_SECTIONS.add(section);
      observer.observe(section);
    }
  }

  /** @param {Element} root */
  function destroyCxCardSlider(root) {
    if (!(root instanceof Element)) return;

    const sections = querySelfAndAll(root, CX_CARD_SLIDER_SECTION_SELECTOR);
    for (const section of sections) {
      if (!(section instanceof Element)) continue;
      CX_CARD_SLIDER_INTERSECTION_OBSERVER?.unobserve?.(section);
      CX_CARD_SLIDER_PENDING_SECTIONS.delete(section);
    }

    const tracks = root.matches(CX_CARD_SLIDER_TRACK_SELECTOR)
      ? [root]
      : /** @type {Element[]} */ (Array.from(root.querySelectorAll(CX_CARD_SLIDER_TRACK_SELECTOR)));

    for (const track of tracks) {
      if (!(track instanceof HTMLElement)) continue;
      const existing = CX_CARD_SLIDER_AUTO_SCROLL_TIMER.get(track);
      if (existing) window.clearInterval(existing);
      CX_CARD_SLIDER_AUTO_SCROLL_TIMER.delete(track);

      CX_CARD_SLIDER_TRACK_TILE_DELTA_CACHE.delete(track);
      CX_CARD_SLIDER_BOUND_TRACKS.delete(track);

      const ro = CX_CARD_SLIDER_TRACK_RESIZE_OBSERVER.get(track);
      ro?.disconnect?.();
      CX_CARD_SLIDER_TRACK_RESIZE_OBSERVER.delete(track);
    }
  }

  /** @type {any} */ (window)['CX'] = /** @type {any} */ (window)['CX'] || {};
  /** @type {any} */ (window)['CX'].initCardSlider = initCxCardSlider;
  /** @type {any} */ (window)['CX'].destroyCardSlider = destroyCxCardSlider;

  function run(root) { scheduleCxCardSlider(root || document); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => run(document), { once: true });
  } else {
    setTimeout(() => run(document), 0);
  }
  document.addEventListener('shopify:section:load', (e) => {
    const detailTarget = e?.detail?.target;
    const root = detailTarget instanceof Element ? detailTarget : e?.target instanceof Element ? e.target : document;
    run(root);
  });
  // Initialize sliders mounted later by the existing lazy Section Rendering API.
  document.addEventListener('lazy-content-section:loaded', (e) => {
    run(e?.target instanceof Element ? e.target : document);
  });
  document.addEventListener('shopify:section:unload', (e) => { if (e?.target instanceof Element) destroyCxCardSlider(e.target); });
  document.addEventListener('shopify:section:reorder', () => run(document));
  document.addEventListener('shopify:block:select', () => run(document));
  document.addEventListener('rebuy.ready', (e) => {
    const detailTarget = e?.detail?.target;
    if (detailTarget instanceof Element) {
      run(detailTarget);
      return;
    }
    document.querySelectorAll('[data-rebuy-id]').forEach((container) => run(container));
  });
})();
